import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Chip,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Collapse,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  AdminPanelSettings as AdminIcon,
  People as AllUsersIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { handleApiError } from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminActivityLogsPage = () => {
  const { token, user } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'tr' ? tr : enUS;

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data states
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    resource: '',
    success: '',
    startDate: '',
    endDate: '',
    endpoint: '',
    userRole: '' // Add user role filter
  });
  const [showFilters, setShowFilters] = useState(false);

  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Load metadata (action types and resources)
  const loadMetadata = useCallback(async () => {
    try {
      const [actionsRes, resourcesRes] = await Promise.all([
        axios.get(`${API_URL}/activity-logs/meta/actions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/activity-logs/meta/resources`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (actionsRes.data.success) {
        setActions(actionsRes.data.data.actions || []);
      }
      if (resourcesRes.data.success) {
        setResources(resourcesRes.data.data.resources || []);
      }
    } catch (error) {
      console.error('Error loading metadata:', error);
    }
  }, [token]);

  // Load activity logs
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);

      // Add filters
      if (filters.action) params.append('action', filters.action);
      if (filters.resource) params.append('resource', filters.resource);
      if (filters.success !== '') params.append('success', filters.success);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.endpoint) params.append('endpoint', filters.endpoint);

      // Filter by user role based on active tab (server-side filtering)
      if (activeTab === 1) {
        // Admin logs only
        params.append('userRole', 'admin');
      } else {
        // Non-admin users only
        params.append('userRole', 'non-admin');
      }

      const response = await axios.get(`${API_URL}/activity-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setLogs(response.data.data.logs || []);
        setTotalCount(response.data.data.pagination.total || 0);
      } else {
        setError(t('activityLogs:errors.loadFailed'));
      }
    } catch (error) {
      console.error('Error loading activity logs:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  }, [token, t, page, rowsPerPage, filters, activeTab]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/activity-logs/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    loadMetadata();
    loadStats();
  }, [loadMetadata, loadStats]);

  // Load logs when filters, pagination, or tab change
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when switching tabs
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      resource: '',
      success: '',
      startDate: '',
      endDate: '',
      endpoint: '',
      userRole: ''
    });
    setPage(0);
  };

  // View log details
  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedLog(null);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Get action chip color
  const getActionColor = (action) => {
    if (action.includes('create')) return 'success';
    if (action.includes('update')) return 'info';
    if (action.includes('delete')) return 'error';
    if (action.includes('login')) return 'primary';
    return 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            {t('activityLogs:title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('activityLogs:actions.toggleFilters')}>
            <IconButton onClick={() => setShowFilters(!showFilters)} color={showFilters ? 'primary' : 'default'}>
              <FilterIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('activityLogs:actions.refresh')}>
            <IconButton onClick={() => { loadLogs(); loadStats(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('activityLogs:stats.totalActions')}
                </Typography>
                <Typography variant="h4">
                  {stats.totalActions?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('activityLogs:stats.successful')}
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.successfulActions?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('activityLogs:stats.failed')}
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.failedActions?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('activityLogs:stats.uniqueUsers')}
                </Typography>
                <Typography variant="h4">
                  {stats.uniqueUserCount?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab
            icon={<AllUsersIcon />}
            iconPosition="start"
            label={t('activityLogs:tabs.allUsers')}
          />
          <Tab
            icon={<AdminIcon />}
            iconPosition="start"
            label={t('activityLogs:tabs.adminOnly')}
          />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('activityLogs:filters.action')}</InputLabel>
                <Select
                  value={filters.action}
                  label={t('activityLogs:filters.action')}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  <MenuItem value="">{t('activityLogs:filters.all')}</MenuItem>
                  {actions.map(action => (
                    <MenuItem key={action} value={action}>{action}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('activityLogs:filters.resource')}</InputLabel>
                <Select
                  value={filters.resource}
                  label={t('activityLogs:filters.resource')}
                  onChange={(e) => handleFilterChange('resource', e.target.value)}
                >
                  <MenuItem value="">{t('activityLogs:filters.all')}</MenuItem>
                  {resources.map(resource => (
                    <MenuItem key={resource} value={resource}>{resource}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('activityLogs:filters.status')}</InputLabel>
                <Select
                  value={filters.success}
                  label={t('activityLogs:filters.status')}
                  onChange={(e) => handleFilterChange('success', e.target.value)}
                >
                  <MenuItem value="">{t('activityLogs:filters.all')}</MenuItem>
                  <MenuItem value="true">{t('activityLogs:filters.successful')}</MenuItem>
                  <MenuItem value="false">{t('activityLogs:filters.failed')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label={t('activityLogs:filters.endpoint')}
                value={filters.endpoint}
                onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                placeholder="/api/..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                <DateTimePicker
                  label={t('activityLogs:filters.startDate')}
                  value={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const year = newValue.getFullYear();
                      const month = String(newValue.getMonth() + 1).padStart(2, '0');
                      const day = String(newValue.getDate()).padStart(2, '0');
                      const hours = String(newValue.getHours()).padStart(2, '0');
                      const minutes = String(newValue.getMinutes()).padStart(2, '0');
                      handleFilterChange('startDate', `${year}-${month}-${day}T${hours}:${minutes}`);
                    } else {
                      handleFilterChange('startDate', '');
                    }
                  }}
                  ampm={false}
                  format="dd/MM/yyyy HH:mm"
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                <DateTimePicker
                  label={t('activityLogs:filters.endDate')}
                  value={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const year = newValue.getFullYear();
                      const month = String(newValue.getMonth() + 1).padStart(2, '0');
                      const day = String(newValue.getDate()).padStart(2, '0');
                      const hours = String(newValue.getHours()).padStart(2, '0');
                      const minutes = String(newValue.getMinutes()).padStart(2, '0');
                      handleFilterChange('endDate', `${year}-${month}-${day}T${hours}:${minutes}`);
                    } else {
                      handleFilterChange('endDate', '');
                    }
                  }}
                  ampm={false}
                  format="dd/MM/yyyy HH:mm"
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ height: '56px' }}
              >
                {t('activityLogs:actions.clearFilters')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Activity Logs Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('activityLogs:table.timestamp')}</TableCell>
                    <TableCell>{t('activityLogs:table.user')}</TableCell>
                    <TableCell>{t('activityLogs:table.action')}</TableCell>
                    <TableCell>{t('activityLogs:table.resource')}</TableCell>
                    <TableCell>{t('activityLogs:table.method')}</TableCell>
                    <TableCell>{t('activityLogs:table.endpoint')}</TableCell>
                    <TableCell>{t('activityLogs:table.status')}</TableCell>
                    <TableCell>{t('activityLogs:table.ipAddress')}</TableCell>
                    <TableCell align="center">{t('activityLogs:table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="textSecondary">
                          {t('activityLogs:noLogs')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log._id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {log.user?.username || log.userSnapshot?.username || t('activityLogs:anonymous')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {log.user?.email || log.userSnapshot?.email || ''}
                            </Typography>
                            {(log.user?.role || log.userSnapshot?.role) && (
                              <Chip
                                label={log.user?.role || log.userSnapshot?.role}
                                size="small"
                                sx={{ ml: 1 }}
                                color={
                                  (log.user?.role === 'admin' || log.userSnapshot?.role === 'admin') ? 'error' :
                                  (log.user?.role === 'trainer' || log.userSnapshot?.role === 'trainer') ? 'primary' :
                                  'default'
                                }
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.action}
                            color={getActionColor(log.action)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={log.resource} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={log.method} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {log.endpoint}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {log.success ? (
                            <Chip
                              icon={<SuccessIcon />}
                              label={log.responseStatus}
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              icon={<ErrorIcon />}
                              label={log.responseStatus}
                              color="error"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {log.ipAddress || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t('activityLogs:actions.viewDetails')}>
                            <IconButton size="small" onClick={() => handleViewDetails(log)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Log Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('activityLogs:details.title')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.timestamp')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatTimestamp(selectedLog.timestamp)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.duration')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedLog.duration ? `${selectedLog.duration}ms` : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.user')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedLog.userSnapshot?.username || t('activityLogs:anonymous')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedLog.userSnapshot?.email || ''}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.role')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedLog.userSnapshot?.role || '-'}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.endpoint')}
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontFamily: 'monospace' }}>
                  {selectedLog.method} {selectedLog.endpoint}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.ipAddress')}
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontFamily: 'monospace' }}>
                  {selectedLog.ipAddress || '-'}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('activityLogs:details.userAgent')}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {selectedLog.userAgent || '-'}
                </Typography>
              </Grid>
              {selectedLog.requestBody && Object.keys(selectedLog.requestBody).length > 0 && (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('activityLogs:details.requestBody')}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {JSON.stringify(selectedLog.requestBody, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              {selectedLog.errorMessage && (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    {t('activityLogs:details.error')}
                  </Typography>
                  <Alert severity="error">
                    {selectedLog.errorMessage}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            {t('activityLogs:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminActivityLogsPage;
